import {
  Input,
  ListPage,
  RefreshButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@skylabs-digital/backoffice-kit';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type ActivityPage as ActivityResult, useActivityActions } from '../api/activity.api';
import { ActivityTable } from '../components/activity/ActivityTable';
import { AppFilter } from '../components/shared/AppFilter';
import { Pager } from '../components/shared/Pager';
import { EVENT_TYPES, eventTypeSchema } from '../schemas/activity.schema';

const LIMIT = 50;
const ANY = '__any__';
const DEBOUNCE_MS = 300;

export function ActivityPage() {
  const api = useActivityActions();
  const [params, setParams] = useSearchParams();
  const appId = params.get('app') ?? undefined;
  const parsedType = eventTypeSchema.safeParse(params.get('type'));
  const type = parsedType.success ? parsedType.data : undefined;
  const q = params.get('q') ?? '';
  const [draft, setDraft] = useState(q);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<ActivityResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const setParam = (key: 'app' | 'type' | 'q', value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setPage(1);
  };

  // La búsqueda viaja a la URL con un pequeño debounce: cada tecla no es un request.
  // biome-ignore lint/correctness/useExhaustiveDependencies: sólo el borrador dispara; setParam lee params frescos.
  useEffect(() => {
    if (draft === q) return;
    const t = setTimeout(() => setParam('q', draft.trim() || undefined), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [draft]);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      setResult(await api.list({ appId, type, q: q || undefined, page, limit: LIMIT }));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to load activity'));
    } finally {
      setRefreshing(false);
    }
  }, [api, appId, type, q, page]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <ListPage
      title="Activity"
      description="Everything that happened to the files: uploads, variants, deletions and quota alerts, as recorded in app_events."
      actions={
        <div className="flex flex-wrap items-center gap-3">
          <AppFilter value={appId} onChange={(v) => setParam('app', v)} />
          <Select
            value={type ?? ANY}
            onValueChange={(v) => setParam('type', v === ANY ? undefined : v)}
          >
            <SelectTrigger className="w-52" aria-label="Event type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any event</SelectItem>
              {EVENT_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Path, file id or event id"
            aria-label="Search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-64"
          />
          <RefreshButton onRefresh={load} isRefreshing={refreshing} label={null} />
        </div>
      }
    >
      <div className="space-y-3">
        {error ? <p className="text-sm text-destructive">{error.message}</p> : null}
        <span className="text-sm text-muted-foreground">
          {result ? `${result.meta.total} events` : ''}
        </span>
        <ActivityTable rows={result?.data ?? []} loading={result === null} error={error} />
        <Pager meta={result?.meta} page={page} onPage={setPage} />
      </div>
    </ListPage>
  );
}
